import React from 'react';
import ReactMarkdown from 'react-markdown';

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;
const SAFE_URL_PATTERN = /^(https?:|mailto:|tel:|#|\/)/i;
const BLOCKED_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta']);

function sanitizeHtml(html) {
  if (typeof window === 'undefined' || !window.DOMParser) return '';

  const documentNode = new window.DOMParser().parseFromString(String(html || ''), 'text/html');
  const nodes = Array.from(documentNode.body.querySelectorAll('*'));

  nodes.forEach((node) => {
    const tagName = node.tagName.toLowerCase();
    if (BLOCKED_TAGS.has(tagName)) {
      node.remove();
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value || '';

      if (name.startsWith('on') || name === 'style' || name === 'srcdoc') {
        node.removeAttribute(attribute.name);
        return;
      }

      if ((name === 'href' || name === 'src') && value && !SAFE_URL_PATTERN.test(value)) {
        node.removeAttribute(attribute.name);
      }
    });

    if (tagName === 'a') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noreferrer');
    }
  });

  return documentNode.body.innerHTML;
}

function renderMarkdown(text, key) {
  return (
    <ReactMarkdown key={key}>
      {text}
    </ReactMarkdown>
  );
}

function renderMixedContent(text) {
  if (typeof window === 'undefined' || !window.DOMParser) {
    return renderMarkdown(text, 'markdown');
  }

  const documentNode = new window.DOMParser().parseFromString(text, 'text/html');
  return Array.from(documentNode.body.childNodes).map((node, index) => {
    if (node.nodeType === window.Node.TEXT_NODE) {
      const value = node.textContent || '';
      return value.trim() ? renderMarkdown(value, `markdown-${index}`) : null;
    }

    if (node.nodeType === window.Node.ELEMENT_NODE) {
      return (
        <div
          key={`html-${index}`}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(node.outerHTML) }}
        />
      );
    }

    return null;
  });
}

export default function MessageContentRenderer({ content }) {
  const text = typeof content === 'string' ? content : String(content || '');

  if (HTML_TAG_PATTERN.test(text)) {
    return <>{renderMixedContent(text)}</>;
  }

  return renderMarkdown(text, 'markdown');
}
